import type {
  SchemaField,
  Workflow,
  WorkflowMetaData,
  WorkflowRevision,
  WorkflowTask,
  WorkflowTaskAction,
  WorkflowTaskCollection,
  WorkflowTaskCollectionRuleContainer,
} from '../interfaces';
import type { SchemaFieldDto } from './schema-fields/types';
import { fromApiSchemaField, toApiSaveSchemaField } from './schema-fields/schema-fields.mapper';

type SchemaCollection<T> = Omit<WorkflowTaskCollection, 'rules'> & {
  rules: (Omit<WorkflowTaskCollectionRuleContainer, 'schema_field'> & { schema_field: T })[];
};
type SchemaTask<T> = Omit<WorkflowTask, 'action'> & {
  action: Omit<WorkflowTaskAction, 'collections'> & { collections?: SchemaCollection<T>[] };
};
type SchemaWorkflow<T> = Omit<Workflow, 'tasks' | 'workflow_revision'> & {
  tasks?: SchemaTask<T>[];
  workflow_revision?: SchemaRevision<T>;
};
type SchemaRevision<T> = Omit<WorkflowRevision, 'workflow' | 'workflow_meta_data'> & {
  workflow: SchemaWorkflow<T>;
  workflow_meta_data: SchemaMetaData<T>;
};
type SchemaMetaData<T> = Omit<WorkflowMetaData, 'workflow_revision' | 'workflow_revisions'> & {
  workflow_revision: SchemaRevision<T>;
  workflow_revisions: SchemaRevision<T>[];
};

export type WorkflowSchemaFieldsDto = SchemaWorkflow<SchemaFieldDto>;
export type WorkflowSchemaFieldsMetaDataDto = SchemaMetaData<SchemaFieldDto>;

function mapWorkflow<T, U>(workflow: SchemaWorkflow<T>, mapField: (field: T) => U): SchemaWorkflow<U> {
  if (!workflow) return workflow;

  return {
    ...workflow,
    ...(workflow.tasks
      ? {
          tasks: workflow.tasks.map((task) => ({
            ...task,
            action: {
              ...task.action,
              ...(task.action?.collections
                ? {
                    collections: task.action.collections.map((collection) => ({
                      ...collection,
                      rules: collection.rules.map(({ schema_field, ...rule }) => ({
                        ...rule,
                        schema_field: schema_field ? mapField(schema_field) : null,
                      })),
                    })),
                  }
                : {}),
            },
          })),
        }
      : {}),
    ...(workflow.workflow_revision ? { workflow_revision: mapRevision(workflow.workflow_revision, mapField) } : {}),
  };
}

function mapRevision<T, U>(revision: SchemaRevision<T>, mapField: (field: T) => U): SchemaRevision<U> {
  if (!revision) return revision;

  return {
    ...revision,
    workflow: mapWorkflow(revision.workflow, mapField),
    workflow_meta_data: mapMetaData(revision.workflow_meta_data, mapField),
  };
}

function mapMetaData<T, U>(metadata: SchemaMetaData<T>, mapField: (field: T) => U): SchemaMetaData<U> {
  if (!metadata) return metadata;

  return {
    ...metadata,
    workflow_revision: mapRevision(metadata.workflow_revision, mapField),
    workflow_revisions: metadata.workflow_revisions?.map((revision) => mapRevision(revision, mapField)),
  };
}

export function fromApiWorkflowSchemaFields(workflow: WorkflowSchemaFieldsDto): Workflow {
  return mapWorkflow(workflow, fromApiSchemaField);
}

export function fromApiWorkflowSchemaFieldsMetaData(metadata: WorkflowSchemaFieldsMetaDataDto): WorkflowMetaData {
  return mapMetaData(metadata, fromApiSchemaField);
}

export function toApiWorkflowSchemaFields(workflow: Workflow): WorkflowSchemaFieldsDto {
  return mapWorkflow(
    workflow,
    (field: SchemaField): SchemaFieldDto => ({
      ...toApiSaveSchemaField(field),
      id: field.id,
      is_archived: field.isArchived,
      is_default: field.isDefault,
    }),
  );
}
